import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/api";
import { toast } from "sonner";

export default function FinishWorkout() {
  const nav = useNavigate();
  const { state } = useLocation();
  const payload = state?.payload;
  const exs = state?.exercises || [];
  const [rpes, setRpes] = useState({});
  const [saving, setSaving] = useState(false);

  const summary = useMemo(() => {
    if (!payload) return null;
    let sets = 0, volume = 0, top = null;
    payload.exercises.forEach((e) => {
      const ex = exs.find((x) => x.exerciseKey === e.exerciseKey);
      e.sets.forEach((s) => {
        if (s.skipped) return;
        sets += 1;
        const m = ex?.each ? 2 : 1;
        volume += s.w * s.reps * m;
        if (!top || s.w > top.w) top = { name: ex?.name || e.exerciseKey, w: s.w };
      });
    });
    return { sets, volume: Math.round(volume), top, minutes: Math.round(payload.durationSec / 60) };
  }, [payload, exs]);

  if (!payload) return (
    <div className="min-h-screen bg-app flex items-center justify-center text-t3">Nothing to finish. <button onClick={() => nav("/workout")} className="text-lime ml-2">Go back</button></div>
  );

  const submit = async () => {
    setSaving(true);
    try {
      const body = {
        ...payload,
        exercises: payload.exercises.map((e) => ({ ...e, rpe: rpes[e.exerciseKey] ?? null })),
      };
      await api.post("/session/finish", body);
      toast.success("Session logged.");
      nav("/dashboard");
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-app max-w-md mx-auto px-5 pt-8 pb-6">
      <div className="overline">Check-in</div>
      <h1 className="font-display text-3xl uppercase mt-2">Well done.</h1>

      <div className="grid grid-cols-3 gap-3 mt-6">
        <Stat k="Time" v={`${summary.minutes}m`} />
        <Stat k="Sets" v={summary.sets} />
        <Stat k="Volume" v={`${summary.volume}kg`} />
      </div>
      {summary.top && (
        <div className="card p-4 mt-3" data-testid="finish-top">
          <div className="overline">Heaviest set</div>
          <div className="font-display text-lg mt-1 uppercase">{summary.top.name}</div>
          <div className="font-mono tabular text-t2 text-sm mt-1">{summary.top.w} kg</div>
        </div>
      )}

      <div className="mt-8">
        <div className="overline mb-3">Rate each exercise · 1 easy · 5 max</div>
        <div className="space-y-3">
          {payload.exercises.filter((e) => !e.skippedEntirely && e.sets.some((s) => !s.skipped)).map((e) => {
            const ex = exs.find((x) => x.exerciseKey === e.exerciseKey);
            const setVol = e.sets.filter((s) => !s.skipped).reduce((a, s) => a + s.w * s.reps * (ex?.each ? 2 : 1), 0);
            return (
              <div key={e.exerciseKey} className="card p-4" data-testid={`rpe-row-${e.exerciseKey}`}>
                <div className="flex justify-between items-baseline">
                  <div className="font-display uppercase">{ex?.name}</div>
                  <div className="font-mono tabular text-t3 text-xs">{e.sets.filter((s) => !s.skipped).length} sets · {Math.round(setVol)}kg</div>
                </div>
                <div className="flex gap-2 mt-3">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} onClick={() => setRpes({ ...rpes, [e.exerciseKey]: n })}
                      className={`chip flex-1 ${rpes[e.exerciseKey] === n ? "selected" : ""}`}
                      data-testid={`rpe-${e.exerciseKey}-${n}`}>{n}</button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-8 space-y-3">
        <button onClick={submit} className="btn btn-primary w-full" disabled={saving} data-testid="finish-submit">{saving ? "…" : "Save session"}</button>
        <button onClick={submit} className="btn btn-outline w-full" disabled={saving} data-testid="finish-skip">Skip rating & save</button>
      </div>
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div className="card p-3">
      <div className="overline">{k}</div>
      <div className="font-display text-2xl mt-1 tabular">{v}</div>
    </div>
  );
}
